import moment from 'moment'
import { fillArray } from 'lib/array/arrayUtils'
import { dateIsAfter, dateIsBefore, endOfMonth, isSameDay, isSameMonth } from 'lib/datetime/dateUtils'

export type CellRanges = {
  start: number
  end: number
}

export function calcGridPositionsForVerticalMonth(firstDateInMonth: Date, startDate?: Date, endDate?: Date): {
  startRow?: number
  endRow?: number
  range?: CellRanges
} {
  const monthEnd = endOfMonth(firstDateInMonth)
  const monthStartOffset = firstDateInMonth.getDate()

  let startRow: number | undefined, endRow: number | undefined, range: CellRanges | undefined
  if (startDate) {
    if (isSameMonth(startDate, firstDateInMonth)) {
      // starts in this month
      startRow = startDate.getDate() - monthStartOffset + 1
    }
  }

  if (endDate) {
    if (isSameMonth(endDate, firstDateInMonth)) {
      // ends in this month
      endRow = endDate.getDate() - monthStartOffset + 2
    }
  }

  if (startDate && endDate) {
    // calcuate our range positions
    if (dateIsBefore(startDate, firstDateInMonth) && dateIsAfter(endDate, monthEnd)) {
      // spans the whole month
      range = {
        start: 1,
        end: monthEnd.getDate() - monthStartOffset,
      }
    } else {
      let start, end
      if (isSameMonth(startDate, firstDateInMonth)) {
        // starts in this month, find the row it starts at
        start = startDate.getDate() - monthStartOffset + 2
      }

      if (dateIsBefore(startDate, firstDateInMonth) && !isSameDay(endDate, firstDateInMonth)) {
        // starts in a previous month, so our range starts on the first day (unless that's also the end)
        start = 1
      }

      if (dateIsAfter(endDate, monthEnd)) {
        // ends in a later away month, so our range is all the way to the end
        end = monthEnd.getDate() - monthStartOffset + 2
      }

      if (isSameMonth(endDate, firstDateInMonth)) {
        // ends in this month, find the row it ends at
        end = endDate.getDate() - monthStartOffset + 1
      }

      if (start && end) {
        range = { start, end }
      }
    }
  }

  return {
    startRow,
    endRow,
    range,
  }
}

/**
 * Create Date obj in user local timezone
 *
 * @param {string} dateString - 2022-01-30
 * @return {Date}
 */
export function getLocalDate(dateString: String) {
  return new Date(`${dateString}T00:00`)
}

/**
 * Get coordinates (month, day)
 */
export function getCalendarDateCoordinates(dateString: string, calendar: App.Calendar) {
  const date = moment.utc(dateString)
  const monthKey = `${date.month() + 1}-${date.year()}`

  const monthIdx = calendar.months.findIndex(month => month.key === monthKey)

  const month = calendar.months[monthIdx]
  const dayIdx = month.days.findIndex(day => day.checkIn === dateString)

  return { monthIdx, dayIdx }
}

export const BEDBANK_CALENDAR_FUTURE_DAYS = 500

/**
 * Day-of-week index for a Monday-first calendar week (0 = Monday ... 6 = Sunday)
 */
export function getMondayFirstWeekdayIndex(date: Date) {
  return (date.getDay() + 6) % 7
}

/**
 * Retrieves the empty cells that should be shown at the start of a calendar month.
 * our month may not start on the first day of the month, because we can't check in
 * on those days (they may be in the past). So we fill in the earliest weeks cells
 */
export function getCalendarMonthEmptyCells(firstDayOfMonth: App.CalendarDay | App.BedbankCalendarDay) {
  if (firstDayOfMonth.day !== 1) {
    const checkIn = moment(firstDayOfMonth.checkIn)
    const firstDay = moment.max(checkIn.clone().startOf('isoWeek'), checkIn.clone().startOf('month'))
    return fillArray(checkIn.date() - firstDay.date(), (index) => ({
      day: firstDay.date() + index,
      column: firstDay.isoWeekday() + index,
    }))
  }
  return []
}

/**
 * Retrieves the empty cells that should be shown at the start of a calendar month.
 * our month may not start on the first day of the month, because we can't check in
 * on those days (they may be in the past). So we fill in the earliest weeks cells
 * This is different from getCalendarMonthEmptyCells because it works from a date
 * string rather than a calendar day object.
 */
export function getSidebarCalendarMonthEmptyCells(firstDateOfMonth: string) {
  const checkIn = moment(firstDateOfMonth)
  if (checkIn.weekday() !== 1) {
    const firstDay = moment.max(checkIn.clone().startOf('isoWeek'), checkIn.clone().startOf('month'))

    return fillArray(checkIn.date() - firstDay.date(), (index) => ({
      day: firstDay.date() + index,
      column: firstDay.weekday() + index,
    }))
  }
  return []
}
